import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type AnswerOption as AnswerOptionBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type AnswerOption = App.PickGraphQL<AnswerOptionBase, 'option' | 'text'>

export type AnswerOptionSelectorProps<TAnswerOption extends AnswerOption> =
  Omit<
    ItemSelectorProps,
    'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
  > & {
    answerOptions: TAnswerOption[]
    isNullSelectable?: boolean
    nullText?: string
    onChange?: (option: TAnswerOption['option'] | Unassigned) => void
    unsetText?: string
    value: TAnswerOption['option'] | Unassigned | null
  }

export const AnswerOptionSelector = React.forwardRef(
  <TAnswerOption extends AnswerOption>(
    {
      answerOptions,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: AnswerOptionSelectorProps<TAnswerOption>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(() => {
      const data = answerOptions.map((answerOption) => ({
        ...answerOption,
        textField: `${answerOption.option} - ${answerOption.text}`,
      }))

      return isNullSelectable
        ? [{ option: null, textField: nullText }, ...data]
        : data
    }, [isNullSelectable, nullText, answerOptions])
    const value = React.useMemo(
      () =>
        data.find(({ option }) => option === valueProp) ?? {
          option: unassignedSymbol,
          textField: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (answerOption: TAnswerOption) => {
        onChange?.(answerOption.option)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="option"
        textField="textField"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
